import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  fetchHomePage,
  mapAbout,
  mapBannersToSlides,
  mapServices,
  mapTestimonials,
  mapWorkingProcesses,
} from "../api/homePage";

const HomePageContext = createContext(null);

const HOME_API_ROUTES = ["/"];

export const HomePageProvider = ({ children }) => {
  const location = useLocation();
  const [rawData, setRawData] = useState(null);
  const [contactOverride, setContactOverride] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const needsHomeApi = HOME_API_ROUTES.includes(location.pathname);

  useEffect(() => {
    if (!needsHomeApi) return undefined;

    let isMounted = true;

    const loadHomePage = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await fetchHomePage();
        if (isMounted) {
          setRawData(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || "Unable to load home page data.");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadHomePage();

    return () => {
      isMounted = false;
    };
  }, [needsHomeApi]);

  const value = useMemo(() => {
    if (!rawData) {
      return {
        loading,
        error,
        banners: [],
        about: null,
        workingProcesses: [],
        testimonials: [],
        contact: contactOverride,
        services: [],
        setContact: setContactOverride,
      };
    }

    return {
      loading,
      error,
      banners: mapBannersToSlides(rawData.banner),
      about: mapAbout(rawData.abouts?.[0]),
      workingProcesses: mapWorkingProcesses(rawData.working_processes),
      testimonials: mapTestimonials(rawData.testimonials),
      contact: contactOverride || rawData.contacts?.[0] || null,
      services: mapServices(rawData.services),
      setContact: setContactOverride,
    };
  }, [rawData, loading, error, contactOverride]);

  return <HomePageContext.Provider value={value}>{children}</HomePageContext.Provider>;
};

export const useHomePage = () => {
  const context = useContext(HomePageContext);
  if (!context) {
    throw new Error("useHomePage must be used within HomePageProvider");
  }
  return context;
};
